import type { CallOffsListResponse, CallOffsQueryParams } from './types';

import {
  keepPreviousData,
  type QueryFunctionContext,
  queryOptions,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { queryKeysCallOff } from '../queryKeys';

import { fetchCallOffs } from './fetchCallOffs';

/**
 * Generates the query options for the call-offs query so they can be shared between the useQuery hook and the prefetching.
 *
 * @param queryParams - The query parameters for fetching call-offs.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getCallOffsQueryOptions = <TData = CallOffsListResponse>({
  queryParams,
  options,
}: {
  queryParams: Partial<CallOffsQueryParams>;
  options?: Omit<
    UseQueryOptions<CallOffsListResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: async ({ signal }: QueryFunctionContext) =>
      fetchCallOffs(queryParams, { signal }),
    queryKey: queryKeysCallOff.getAll(queryParams),
    staleTime: ms('60s'),
    ...options,
  });

/**
 * React Query based custom hook for getting the data for all call-offs with given query parameters.
 * Handles pagination and provides stable data while fetching new pages.
 *
 * @param queryParams - The query parameters for fetching call-offs.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/Call-Offs/operation/list_calloffs_calloffs_get
 */
export const useQueryCallOffs = (
  queryParams: Partial<CallOffsQueryParams>,
  options?: Omit<
    UseQueryOptions<CallOffsListResponse, Error, CallOffsListResponse>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    ...getCallOffsQueryOptions<CallOffsListResponse>({
      options,
      queryParams,
    }),
    placeholderData: keepPreviousData,
  });
};
